import { useConfig } from "@/context/ConfigContext";
import { useCallback, useEffect, useState } from "react";

export function useProductComments(options = {}) {
  const { productUuid, enabled = true } = options;
  const { configData } = useConfig();
  const [comments, setComments] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(async () => {
    if (!productUuid || !configData?.team_id) return;
    try {
      setLoading(true);
      setError(null);

      const teamId = configData.team_id;
      const proxyUrl = `/api/products?team_id=${teamId}&type=comments&product_id=${productUuid}`;

      const response = await fetch(proxyUrl, {
        method: "GET",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        cache: "no-cache",
      });

      if (!response.ok) {
        let errorData;
        try { errorData = await response.json(); } catch { errorData = { message: response.statusText }; }
        throw new Error(errorData.message || response.statusText);
      }

      const data = await response.json();
      if (Array.isArray(data.data)) {
        setComments(data.data);
        setMeta(data.meta || null);
      } else {
        setComments([]);
      }
    } catch (err) {
      setError(err.message || "Gagal mengambil komentar");
    } finally {
      setLoading(false);
    }
  }, [configData?.team_id, productUuid]);

  useEffect(() => {
    if (!enabled || !productUuid || !configData?.team_id) return;
    fetchComments();
  }, [enabled, fetchComments]);

  const createComment = useCallback(async ({ content, type = "discussion", replyToUuid = null }) => {
    if (!productUuid || !content?.trim()) return { success: false, message: "Isi komentar tidak boleh kosong" };
    try {
      setSubmitting(true);
      const payload = {
        product_uuid: productUuid,
        type,
        title: "Product",
        content: content.trim(),
        ...(replyToUuid ? { reply_to: replyToUuid } : {}),
      };

      const response = await fetch("/api/comments", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        return { success: false, message: data.message || "Gagal mengirim komentar" };
      }

      // Refresh comments list
      await fetchComments();
      return { success: true, data: data.data };
    } catch (err) {
      return { success: false, message: err.message || "Gagal mengirim komentar" };
    } finally {
      setSubmitting(false);
    }
  }, [productUuid, fetchComments]);

  return { comments, meta, loading, error, submitting, createComment, refetch: fetchComments };
}
